import { ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

import { catchError, combineLatest, filter, map, of, Subscription, take, throwError, shareReplay } from 'rxjs';
import { isNil } from 'lodash';

import { CurrencyConversionService } from '../../../../Core/Users/services/currency-conversion.service';
import { currencySymbolMap, UserCurrency } from '../../../../Core/Users/enums/user-currency';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { safeDivide } from '../../../../Core/utils';

import { abbreviateNumber } from '../../../../Common/Misc/helpers/formatting/number.helper';
import { AgprMath } from '../../../../Common/Misc/helpers/math/math-helper';
import {
    ChartMetricsEnum,
    chartMetricsToCoverageMetricIconsMap,
    chartMetricsToDateAndValueXYChartUnitMap
} from '../../../../Common/Reports/enums/chart-metrics.enum';
import { ChartMetadataModel } from '../../../../Common/Charts/models/chart-metadata.model';
import { CoverageMetricChartDisplayElement } from '../../../../Common/Reports/models/coverage-metric-chart-display-element.model';
import { CoverageMetricChartDisplaySettings } from '../../../../Common/Charts/models/coverage-metric-chart-display-settings.model';
import { CoverageMetricChartStyles } from '../../../../Common/Charts/models/coverage-metric-chart-styles.model';
import { DataPointModel } from '../../../../Common/Charts/models/data-point.model';
import { DateValueXYChart } from '../../../../Common/Charts/models/date-value-x-y-chart.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ReportBlockTypeEnum } from '../../../../Common/Reports/enums/report-block-type.enum';
import { ReportsChartOptionsService } from '../../../../Common/Reports/services/reports-chart-options.service';
import { ReportsChartSeriesConverterService } from '../../../../Common/Reports/services/reports-chart-series-converter.service';
import { ReportSeriesModel } from '../../../../Common/Reports/models/report-series.model';
import { ReportsSeriesPreviewService } from '../../../../Common/Reports/services/report-series-preview.service';
import { ReportsChartComponent } from '../ReportsChart/reports-chart.component';
import { ChartIconEnum, chartIconToCoverageIconMap } from '../../../../Common/Reports/enums/chart-icon.enum';
import { DataListChartOptionsModel } from '../../../../Common/Charts/models/data-list-chart-options.model';



@Component({
    selector: 'agpr-coverage-metric-chart',
    templateUrl: './coverage-metric-chart.component.html',
    styleUrls: ['./coverage-metric-chart.component.scss'],
    standalone: false
})
export class CoverageMetricChartComponent implements OnInit, OnDestroy, OnChanges {

    @Input() displayElement: CoverageMetricChartDisplayElement;
    @Input() series: ReportSeriesModel;
    @Input() accentColor: string;
    @Input() dateChartLabelOptions: DataListChartOptionsModel;
    @Input() dateRangeLabelOverride: string;
    @Output() dataChange = new EventEmitter<boolean>();
    @Output() sourceDeleted: EventEmitter<boolean> = new EventEmitter();

    displayTitle: boolean;
    title: string;
    displayIcon: boolean;
    icon: string;
    iconColor: string;
    displayTitleLine: boolean;
    displayDateRange: boolean;
    dateRangeLabel: string;
    displaySourceLink: boolean;
    sourceRouterLink: string;
    sourceRouterLinkQueryParams: any;
    displaySourceAsLink: boolean;
    displayPreviousPeriod: boolean;
    displaySparkChart: boolean;

    currency: UserCurrency;
    currencySymbol: string;
    percentSign: string;
    currentValue: number;
    currentValueString: string;
    previousValue: number;
    deltaPercent: number;
    previousTooltip: string;
    hasData: boolean;
    loading: boolean = true;
    showInlineDateSource: boolean = true;

    chart: DateValueXYChart;

    preferences: PreferencesModel;

    subscriptions: Subscription[] = [];
    valueSubscription: Subscription;
    chartReadySubscription: Subscription;

    @ViewChild('chartComponent') chartComponent: ReportsChartComponent;

    constructor(
        public currencyConversionService: CurrencyConversionService,
        public currentUserStateService: CurrentUserStateService,
        public localizationService: LocalizationService,
        public reportsSeriesPreviewService: ReportsSeriesPreviewService,
        public reportsChartOptionsService: ReportsChartOptionsService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public changeDetectorRef: ChangeDetectorRef,
        private zone: NgZone
    ) {
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().subscribe(x => this.preferences = x)
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
        this.valueSubscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.updateChart();
        if (this.series) {
            this.updateDateChart();
            this.watchData();
        }

        // we don't set the size in briefings, and in briefings, it's making the
        // center panel way to wide having the date & source in line
        if (this.displayElement?.size?.height && this.displayElement?.size?.width) {
            this.showInlineDateSource = true;
        } else {
            this.showInlineDateSource = false;
        }
    }

    updateChart(): void {
        if (this.displayElement) {
            this.updateTitleLine();
            this.updateDateRange();
            this.updateSourceLink();
            this.updateSparkChart();
            this.updateCurrency();
        }
        if (!this.series) {
            this.dataChange.emit(true);
        }
        this.changeDetectorRef.detectChanges();
    }

    updateTitleLine(): void {
        this.displayTitle = this.displayElement.chartHeaderSettings.title;
        this.title = this.displayElement.title;
        this.displayIcon = this.displayElement.chartHeaderSettings.icon;
        this.icon = chartMetricsToCoverageMetricIconsMap.get(this.displayElement.titleIcon as ChartMetricsEnum)
            ?? chartIconToCoverageIconMap.get(this.displayElement.titleIcon as ChartIconEnum);
        this.iconColor = this.displayElement.styles.colorMap['accentColor'];
        this.displayTitleLine = this.displayTitle || this.displayIcon;
        this.displayPreviousPeriod = this.displayElement.metric === ChartMetricsEnum.custom
            ? !isNil((this.displayElement.chartHeaderSettings as CoverageMetricChartDisplaySettings).customPreviousValue)
            : this.displayElement.chartHeaderSettings.previousPeriod;
    }

    updateDateRange(): void {
        this.displayDateRange = this.displayElement.chartHeaderSettings.dateRange;
        if (this.dateRangeLabelOverride) {
            this.displayElement.dateRangeLabel = this.dateRangeLabelOverride;
        } else {
            this.displayElement.dateRangeLabel = this.reportsSeriesPreviewService.dateRangeToString(
                this.displayElement.series,
                null,
                this.displayElement.chartHeaderSettings.dateFormat
            );
        }
        this.dateRangeLabel = this.displayElement.dateRangeLabel;
    }

    updateSourceLink(): void {
        this.displaySourceLink = this.displayElement.chartHeaderSettings.sourceLink;
        this.displaySourceAsLink = this.displayElement.chartHeaderSettings.displaySourceAsLink;
        if (this.displaySourceAsLink && this.displayElement?.series?.[0]) {
            const urlParts = this.displayElement.series[0].getMentionSeriesUrl()?.split('?filters=');
            this.sourceRouterLink = '/' + urlParts?.[0];
            this.sourceRouterLinkQueryParams = { filters: urlParts?.[1] };
        }
    }

    updateSparkChart(): void {
        this.displaySparkChart = this.displayElement.chartHeaderSettings.sparkChart && this.displayElement.metric !== ChartMetricsEnum.custom;
        this.changeDetectorRef.detectChanges();
    }

    updateCurrency(): void {
        this.currency = this.displayElement?.styles?.currency;
        if (!this.currency && this.preferences) {
            this.currency = this.preferences.currency;
        }
        if (this.displayElement?.styles && this.currency) {
            this.displayElement.styles.currency = this.currency;
        }
        if (this.displayElement.metric === ChartMetricsEnum.ave) {
            this.currencySymbol = currencySymbolMap.get(this.currency);
        } else {
            this.currencySymbol = '';
        }
    }

    watchData(): void {
        if (this.displayElement.metric === ChartMetricsEnum.custom) {
            this.currentValue = this.displayElement.customCurrentValue;
            this.currentValueString = abbreviateNumber(this.currentValue);
            this.hasData = !isNil(this.currentValue);
            this.previousValue = this.displayElement.chartHeaderSettings.customPreviousValue;
            this.deltaPercent = this.percentageChange(this.currentValue, this.previousValue, 1);
            this.previousTooltip = this.getPreviousTooltip(this.title, this.deltaPercent);
            this.loading = false;
            this.changeDetectorRef.detectChanges();
            this.dataChange.emit(true);
        } else {
            if (this.valueSubscription) {
                this.valueSubscription.unsubscribe();
                this.valueSubscription = null;
            }
            let data = [];
            let previousData = [];
            this.loading = true;
            const currentPeriodDataProvider$ = this.series.dataProvider.pipe(
                filter(v => !!v),
                map(itemData => {
                    this.zone.runOutsideAngular(() => {
                        data = itemData;
                        return of(itemData);
                    });
                }),
                catchError(err => {
                    this.zone.runOutsideAngular(() => {
                        this.loading = false;
                    });
                    return throwError(() => new Error(err));
                })
            );
            const previousPeriodDataProvider$ = this.series.hasPreviousPeriod ?
                this.series.previousPeriodDataProvider.pipe(
                    filter(v => !!v),
                    map(itemData => {
                        this.zone.runOutsideAngular(() => {
                            previousData = itemData;
                            return of(itemData);
                        });
                    }),
                    catchError(err => {
                        this.zone.runOutsideAngular(() => {
                            this.loading = false;
                        });
                        if (err.message === 'Data source no longer available') {
                            this.sourceDeleted.emit(true);
                        }

                        return throwError(() => new Error(err));
                    })
                )
                : of([]);
            this.valueSubscription = combineLatest([
                currentPeriodDataProvider$,
                previousPeriodDataProvider$
            ]).subscribe({
                next: ([currentPeriodData, previousPeriodData]) => {
                    this.hasData = !!data.length || !!previousData.length;
                    if (!this.hasData) {
                        this.loading = false;
                        this.dataChange.emit(true);
                        this.changeDetectorRef.detectChanges();
                        return;
                    }
                    this.currentValue = data.reduce((x, y) => x + (y. value || 0), 0);
                    this.previousValue = previousData.reduce((x, y) => x + (y. value || 0), 0);
                    this.percentSign ='';
                    switch (this.displayElement.metric) {
                        case ChartMetricsEnum.positiveSentimentPercentage:
                        case ChartMetricsEnum.neutralSentimentPercentage:
                        case ChartMetricsEnum.negativeSentimentPercentage:
                            this.recalculateAggregateValues(data, previousData, (x) => x['mentionCount'] / 100);
                            this.percentSign = '%';
                            break;
                        case ChartMetricsEnum.ENGAGEMENTS_PER_POST:
                            this.recalculateAggregateValues(data, previousData, (x) => x['mentionCount']);
                            break;
                        case ChartMetricsEnum.ENGAGEMENT_RATE:
                            this.recalculateAggregateValues(data, previousData, (x) => x['mentionCount']);
                            this.percentSign = '%';
                            break;
                        case ChartMetricsEnum.AVERAGE_DOMAIN_AUTHORITY:
                            this.recalculateAggregateValues(data, previousData, (x) => x['mentionCount']);
                            break;
                    }
                    this.currentValueString = abbreviateNumber(this.currentValue);
                    this.deltaPercent = this.percentageChange(this.currentValue, this.previousValue, 1);
                    this.previousTooltip = this.getPreviousTooltip(this.title, this.deltaPercent);
                    this.loading = false;
                    this.changeDetectorRef.detectChanges();

                    if (!this.displaySparkChart) {
                        this.dataChange.emit(true);
                    }
                }
            });
        }
    }

    recalculateAggregateValues(
        data: DataPointModel[],
        previousData: DataPointModel[],
        getFallbackValue: (x: DataPointModel) => number
    ): void {
        const currentNumeratorTotal = data.reduce((x, y) => x + (y['secondaryValue'] || 0), 0);
        const currentDenominatorTotal = data.reduce((x, y) => {
            let currentValue = safeDivide(y['secondaryValue'], y['value']);
            if (!y['secondaryValue'] && !y['value']) {
                currentValue = getFallbackValue(y);
            }
            return x + (currentValue || 0);
        }, 0);
        this.currentValue = +safeDivide(currentNumeratorTotal, currentDenominatorTotal);
        const previousNumeratorTotal = previousData.reduce((x, y) => x + (y['secondaryValue'] || 0), 0);
        const previousDenominatorTotal = previousData.reduce((x, y) => {
            let previousValue = safeDivide(y['secondaryValue'], y['value']);
            if (!y['secondaryValue'] && !y['value']) {
                previousValue = getFallbackValue(y);
            }
            return x + (previousValue || 0);
        }, 0);
        this.previousValue = +safeDivide(previousNumeratorTotal, previousDenominatorTotal);
    }

    percentageChange (currentData: number, previousData: number, decimalPlaces: number = 1): number {
        if (currentData === 0) {
            // if both currentData & previousData are zero, we want to return undefined (aka current & previous data is the same)
            if (previousData === 0) {
                return undefined;
            }

            // if currentData is zero, we want to return negative infinity, this is being used in the coverage summary chart
            return -Infinity;
        }
        // if previousData is zero, we want to return infinity, this is being used in the coverage summary chart
        if (previousData === 0) {
            return Infinity;
        }
        // When dealing with numbers with different signs measures the % change after you cross the zero line,
        // as the % change before you cross the zero line is always 100% ... spooky
        return AgprMath.round(100 * ((currentData - previousData) / Math.abs(previousData)), decimalPlaces);
    };

    getPreviousTooltip(heading: string, percent: number): string {
        let tooltip = this.dateChartLabelOptions?.neutralTooltip ?? this.localizationService.transform('Reports.chart.bubbleTooltip.neutral');

        if (percent > 0) {
            tooltip = this.dateChartLabelOptions?.positiveTooltip ?? this.localizationService.transform('Reports.chart.bubbleTooltip.positive');
        } else if (percent < 0) {
            tooltip = this.dateChartLabelOptions?.negativeTooltip ?? this.localizationService.transform('Reports.chart.bubbleTooltip.negative');
        }

        return tooltip.replace('{{metric}}', heading).replace('{{value}}', Math.abs(percent) + '');
    }

    updateDateChart(): void {
        const series = [this.series];
        let unit;
        if (this.displayElement.metric) {
            unit = chartMetricsToDateAndValueXYChartUnitMap.get(this.displayElement.metric);
        }
        const options = this.reportsChartOptionsService.getCoverageTrendChartOptions(
            unit,
            this.displayElement,
            [this.displayElement.metric]
        );
        options.isSparkline = true;
        options.autoResize = true;
        options.styles = new CoverageMetricChartStyles(options.styles);
        options.styles.customPadding = {
            top: 0,
            right: 5,
            bottom: 0,
            left: 5
        };
        const colorMap = options.styles.colorMap;
        colorMap['currentPeriod'] = colorMap['accentColor'];
        if (this.series.hasPreviousPeriod) {
            colorMap['previousPeriod'] = colorMap['accentColor'];
        }

        const metadata = {
            type: ReportBlockTypeEnum.COVERAGE_METRIC
        } as ChartMetadataModel;

        const chartSeries = this.reportsChartSeriesConverterService.getCoverageMetricChartSeries(this.series);
        this.chart = new DateValueXYChart(
            series,
            options,
            metadata,
            chartSeries
        );
        if (this.chartReadySubscription) {
            this.chartReadySubscription.unsubscribe();
        }
        this.chartReadySubscription = this.chart.ready.subscribe((event) => {
            this.updateSparkChart();

            // HACK for CIB, we use dataChange to generate the images, but sometimes the image generates when the line chart
            // has not been loaded yet, so adding a setTimeout allows for it to work, we also don't want to generate the image
            // if there is no date (aka initial load), tried with requestAnimationFrame, but only the setTimeout seems to work consitently
            if (!isNil(this.currentValue) && this.chart?.amchart?.data?.length) {
                setTimeout(() => {
                    this.dataChange.emit(true);
                }, 100);
            }
        });
    }
}
